/**
 * Clean capture mode - hides every screen-space UI layer while leaving the
 * Babylon canvas and explicitly preserved in-world DOM projections visible.
 *
 * State is deliberately page-local. The class-based visibility gate does not
 * mutate any surface's own display state, so restoring the interface reveals
 * the latest underlying HUD state rather than a stale snapshot.
 */

import { uiPanelsStore } from '../state/uiPanelsStore';

const BODY_CLASS = 'oggi-capture-mode';
const PRESERVE_ATTRIBUTE = 'data-oggi-capture-preserve';
const STYLE_ID = 'oggi-capture-mode-styles';
const HOT_CORNER_SIZE = 56;

let active = false;
let installed = false;

function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    body.${BODY_CLASS} > :not(#renderCanvas):not([${PRESERVE_ATTRIBUTE}]) {
      visibility: hidden !important;
      pointer-events: none !important;
    }

    body.${BODY_CLASS},
    body.${BODY_CLASS} #renderCanvas {
      cursor: none !important;
    }
  `;
  document.head.appendChild(style);
}

/** @param {EventTarget|null} target */
function isTypingTarget(target) {
  const el = /** @type {HTMLElement|null} */ (target);
  const tag = el?.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!el?.isContentEditable;
}

function anyPanelOpen() {
  return Object.values(uiPanelsStore.getState().open).some(Boolean);
}

function dispatchChange() {
  if (typeof CustomEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent('oggi:capture-mode-changed', { detail: { active } }));
}

/**
 * Enter clean capture mode when the 3D scene is ready and no panel owns input.
 * @returns {boolean} true when capture mode is active after the call
 */
export function enterCaptureMode() {
  if (active) return true;
  if (window.SCENE_READY !== true || anyPanelOpen() || !document.body) return false;
  installCaptureMode();
  active = true;
  document.body.classList.add(BODY_CLASS);
  /** @type {HTMLElement|null} */ (document.activeElement)?.blur?.();
  dispatchChange();
  return true;
}

/** @returns {boolean} false after the call */
export function exitCaptureMode() {
  if (!active) return false;
  active = false;
  document.body?.classList.remove(BODY_CLASS);
  dispatchChange();
  return false;
}

/** @returns {boolean} the resulting active state */
export function toggleCaptureMode() {
  return active ? exitCaptureMode() : enterCaptureMode();
}

/** @returns {boolean} */
export function isCaptureMode() {
  return active;
}

/** @param {KeyboardEvent} event */
function onKeyDown(event) {
  if (isTypingTarget(event.target) || event.repeat) return;

  const captureShortcut =
    event.code === 'KeyH' && event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey;

  if (captureShortcut) {
    let handled;
    if (active) {
      exitCaptureMode();
      handled = true;
    } else {
      handled = enterCaptureMode();
    }
    if (handled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    return;
  }

  // Let SettingsMenu own the same Escape after the interface is restored.
  // In Classic pointer-lock mode browsers may swallow the keydown; the
  // pointerlockchange listener below handles that route.
  if (active && event.key === 'Escape') exitCaptureMode();
}

/** @param {PointerEvent} event */
function onPointerDown(event) {
  if (!active) return;
  if (event.clientX < window.innerWidth - HOT_CORNER_SIZE || event.clientY > HOT_CORNER_SIZE)
    return;
  exitCaptureMode();
  event.preventDefault();
  event.stopImmediatePropagation();
}

function onPointerLockChange() {
  if (active && !document.pointerLockElement) exitCaptureMode();
}

/** Install global capture-mode input handling. Idempotent for the page life. */
export function installCaptureMode() {
  if (installed) return;
  installed = true;
  ensureStyles();
  window.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('pointerlockchange', onPointerLockChange);
}

export const CAPTURE_MODE_BODY_CLASS = BODY_CLASS;
export const CAPTURE_MODE_PRESERVE_ATTRIBUTE = PRESERVE_ATTRIBUTE;
export const CAPTURE_MODE_HOT_CORNER_SIZE = HOT_CORNER_SIZE;
